import React, {useCallback, useState} from 'react';
import {Pressable, ScrollView, StyleSheet, View} from 'react-native';
import type {Meta} from '@storybook/react-native-web-vite';

import Text from '../../../components/themed/Text';
import theme from '../../../style/theme';

import AnimatedBottle, {FILL_DURATION_MS} from './AnimatedBottle';
import BottleRow from './BottleRow';

/**
 * Stories for the coffee card fill animation.
 *
 * The animation ships in `ExclusiveOfferDetailScreen/CoffeeCard`; this is the
 * bench for judging the motion in isolation.
 */

const BOTTLE_HEIGHT = 160;
const FILL_COLOR = theme.colour.general.primary;

/**
 * Controls use the brand's secondary colour, not the primary.
 * SipnSave's primary is #FFDD00 — white label text on it is unreadable, and the
 * primary is already spoken for here as the liquid.
 */
const CONTROL_COLOR = theme.colour.general.secondary;

const meta = {
  title: 'Components/Rewards/Coffee Card Bottle',
} satisfies Meta;

export default meta;

const Button = ({label, onPress}: {label: string; onPress: () => void}) => (
  <Pressable style={styles.button} onPress={onPress}>
    <Text style={styles.buttonLabel}>{label}</Text>
  </Pressable>
);

const Section = ({
  title,
  note,
  children,
}: {
  title: string;
  note?: string;
  children: React.ReactNode;
}) => (
  <View style={styles.section}>
    <Text style={styles.sectionTitle}>{title}</Text>
    {note ? <Text style={styles.sectionNote}>{note}</Text> : null}
    {children}
  </View>
);

/**
 * The main bench: earn drinks, redeem to start a fresh cycle, and remount to
 * confirm the entry animation runs every time.
 */
export const Playground = {
  render: () => {
    const TARGET = 5;
    const [purchased, setPurchased] = useState(0);
    const [cycleId, setCycleId] = useState(1);
    const [rowKey, setRowKey] = useState(0);
    const [lastRun, setLastRun] = useState<string>('—');

    const startedAt = React.useRef(0);
    const replay = useCallback(() => {
      startedAt.current = Date.now();
      setLastRun('running…');
      setRowKey(k => k + 1);
    }, []);

    const onRowComplete = useCallback(() => {
      const elapsed = startedAt.current ? Date.now() - startedAt.current : 0;
      setLastRun(`${elapsed}ms for ${purchased} bottle(s)`);
    }, [purchased]);

    return (
      <ScrollView contentContainerStyle={styles.screen}>
        <Section
          title="Recursive coffee card"
          note={`Cycle ${cycleId} · ${purchased} of ${TARGET} earned · every bottle fills together in ${FILL_DURATION_MS}ms`}>
          <BottleRow
            key={`${cycleId}-${rowKey}`}
            filledCount={purchased}
            emptyCount={TARGET - purchased}
            height={BOTTLE_HEIGHT}
            color={FILL_COLOR}
            onRowComplete={onRowComplete}
          />
          <Text style={styles.readout}>Last run: {lastRun}</Text>
        </Section>

        <View style={styles.controls}>
          <Button
            label="Buy a drink"
            onPress={() => {
              setPurchased(p => Math.min(TARGET, p + 1));
              replay();
            }}
          />
          <Button
            label="Redeem → new cycle"
            onPress={() => {
              setPurchased(0);
              setCycleId(c => c + 1);
              setLastRun('—');
            }}
          />
          <Button label="Remount" onPress={replay} />
        </View>

        <Text style={styles.footnote}>
          The fill runs on every mount — remount, buy a drink or redeem and the earned bottles pour
          again. Nothing is remembered between visits. Note that earning a drink mid-cycle re-pours
          the whole row, not just the new bottle.
        </Text>
      </ScrollView>
    );
  },
};

/** Every count takes the same time, because the bottles fill together. */
export const ParallelFill = {
  render: () => {
    const [nonce, setNonce] = useState(0);
    const counts = [1, 2, 3, 4, 5];

    return (
      <ScrollView contentContainerStyle={styles.screen}>
        <Section
          title="Parallel fill"
          note={`All earned bottles start on the same frame, so every row below finishes in ${FILL_DURATION_MS}ms — the count makes no difference.`}>
          {counts.map(count => (
            <View key={count} style={styles.budgetRow}>
              <Text style={styles.budgetLabel}>
                {count} bottle{count > 1 ? 's' : ''} · {FILL_DURATION_MS}ms
              </Text>
              <BottleRow
                key={`${count}-${nonce}`}
                filledCount={count}
                emptyCount={0}
                height={110}
                color={FILL_COLOR}
              />
            </View>
          ))}
        </Section>
        <View style={styles.controls}>
          <Button label="Replay all" onPress={() => setNonce(n => n + 1)} />
        </View>
      </ScrollView>
    );
  },
};

/**
 * The two bottle styles side by side (DIS-1733).
 *
 * The beer bottle differs in three ways at once — a longneck silhouette, a
 * foam crown that tops out level with the wine bottle's liquid, and
 * carbonation in place of slow bubbles — so it is only worth judging against
 * the wine bottle it sits beside on the card.
 */
export const BottleStyles = {
  render: () => {
    const [nonce, setNonce] = useState(0);

    return (
      <ScrollView contentContainerStyle={styles.screen}>
        <Section
          title="Wine and beer"
          note="Both rows pour on the same 650ms budget. Replay to compare the two head-on.">
          <View style={styles.comparison}>
            <View style={styles.comparisonItem}>
              <BottleRow
                key={`wine-${nonce}`}
                variant="wine"
                filledCount={2}
                emptyCount={1}
                height={BOTTLE_HEIGHT}
                color={FILL_COLOR}
              />
              <Text style={styles.caption}>wine — no head, slow bubbles</Text>
            </View>
            <View style={styles.comparisonItem}>
              <BottleRow
                key={`beer-${nonce}`}
                variant="beer"
                filledCount={2}
                emptyCount={1}
                height={BOTTLE_HEIGHT}
                color={FILL_COLOR}
              />
              <Text style={styles.caption}>beer — foam crown, carbonation</Text>
            </View>
          </View>
        </Section>
        <View style={styles.controls}>
          <Button label="Replay both" onPress={() => setNonce(n => n + 1)} />
        </View>
      </ScrollView>
    );
  },
};

/** Idle motion on its own, with the entry fill out of the way. */
export const IdleMotionOnly = {
  render: () => (
    <ScrollView contentContainerStyle={styles.screen}>
      <Section
        title="Idle slosh and bubbles"
        note="No entry fill. This is what a settled, earned bottle does on its own.">
        <View style={styles.comparison}>
          <View style={styles.comparisonItem}>
            <AnimatedBottle height={BOTTLE_HEIGHT} color={FILL_COLOR} filled idleMotion />
            <Text style={styles.caption}>Idle motion on</Text>
          </View>
          <View style={styles.comparisonItem}>
            <AnimatedBottle height={BOTTLE_HEIGHT} color={FILL_COLOR} filled idleMotion={false} />
            <Text style={styles.caption}>Idle motion off</Text>
          </View>
          <View style={styles.comparisonItem}>
            <AnimatedBottle height={BOTTLE_HEIGHT} color={FILL_COLOR} filled={false} />
            <Text style={styles.caption}>Empty</Text>
          </View>
          <View style={styles.comparisonItem}>
            <AnimatedBottle
              variant="beer"
              height={BOTTLE_HEIGHT}
              color={FILL_COLOR}
              filled
              idleMotion
            />
            <Text style={styles.caption}>Beer, idle motion on</Text>
          </View>
        </View>
      </Section>
    </ScrollView>
  ),
};

const styles = StyleSheet.create({
  screen: {
    padding: 24,
    gap: 24,
  },
  section: {
    gap: 12,
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: '700',
    color: '#111827',
  },
  sectionNote: {
    fontSize: 13,
    color: '#6b7280',
  },
  controls: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  button: {
    backgroundColor: CONTROL_COLOR,
    paddingHorizontal: 14,
    paddingVertical: 10,
    borderRadius: 8,
  },
  buttonLabel: {
    color: '#ffffff',
    fontSize: 13,
    fontWeight: '600',
  },
  readout: {
    fontSize: 13,
    color: '#374151',
    textAlign: 'center',
  },
  footnote: {
    fontSize: 12,
    color: '#6b7280',
    lineHeight: 18,
  },
  budgetRow: {
    gap: 6,
    marginBottom: 12,
  },
  budgetLabel: {
    fontSize: 12,
    color: '#6b7280',
  },
  comparison: {
    flexDirection: 'row',
    gap: 24,
    flexWrap: 'wrap',
    alignItems: 'flex-start',
  },
  comparisonItem: {
    alignItems: 'center',
    gap: 4,
  },
  caption: {
    fontSize: 12,
    fontWeight: '600',
    color: '#374151',
  },
  captionNote: {
    fontSize: 11,
    color: '#9ca3af',
  },
});
